import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Users, Eye, Globe, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n, lang) =>
  Math.max(0, Math.floor(n)).toLocaleString(lang === "ar" ? "ar-SA" : "en-US");

function useCountUp(value, duration = 1800) {
  const [n, setN] = useState(0);
  const rafId = useRef(0);
  useEffect(() => {
    if (!value) { setN(0); return; }
    const from = n;
    const to = value;
    const t0 = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setN(Math.round(from + (to - from) * eased));
      if (t < 1) rafId.current = requestAnimationFrame(tick);
    };
    rafId.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return n;
}

export default function VisitorCounter({ lang = "ar", theme = "dark", embed = false }) {
  const dark = theme === "dark";
  const ar = lang === "ar";
  const font = ar ? "font-arabic" : "font-outfit";
  const [data, setData] = useState({ total: 0, today: 0 });
  const [online, setOnline] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const r = await axios.get(`${API}/visits/count`);
        if (!cancelled) setData({ total: r.data.total || 0, today: r.data.today || 0 });
      } catch {}
    };
    const track = async () => {
      const today = new Date().toISOString().slice(0, 10);
      const last = localStorage.getItem("pl_visit_day");
      if (last === today) return;
      try {
        await axios.post(`${API}/visits/track`);
        localStorage.setItem("pl_visit_day", today);
      } catch {}
    };
    (async () => { await track(); await fetchCount(); })();
    const seedOnline = 5 + Math.floor(Math.random() * 10);
    setOnline(seedOnline);
    const walk = setInterval(() => {
      setOnline((n) => {
        const delta = Math.random() < 0.5 ? -1 : 1;
        return Math.min(22, Math.max(4, n + delta));
      });
    }, 7000);
    const refresh = setInterval(fetchCount, 60000);
    return () => { cancelled = true; clearInterval(walk); clearInterval(refresh); };
  }, []);

  const total = useCountUp(data.total, 1800);
  const today = useCountUp(data.today, 1200);

  const BigStat = ({ icon: Icon, label, value, sublabel, pulse = false, testId }) => (
    <div
      data-testid={testId}
      className={`relative overflow-hidden rounded-2xl p-2.5 sm:p-3 border transition-all hover:scale-[1.02] ${
        dark
          ? "bg-[#1f2a47] border-[#2e3a5c] hover:border-[#18c98f]/60"
          : "bg-white border-[#e5e5ea] hover:border-[#18c98f]/60"
      }`}
    >
      {/* Soft green glow */}
      <div className="absolute -top-10 -end-10 h-20 w-20 rounded-full bg-[#18c98f]/15 blur-2xl pointer-events-none" />
      <div className="relative flex flex-col items-center text-center gap-1.5">
        <div className={`relative shrink-0 h-8 w-8 rounded-xl inline-flex items-center justify-center ${
          pulse ? "bg-[#18c98f] text-white" : "bg-[#18c98f]/15 text-[#18c98f]"
        }`}>
          <Icon className="h-4 w-4" strokeWidth={2.4} />
          {pulse && (
            <span className="absolute -top-0.5 -end-0.5 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#18c98f] opacity-70" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#18c98f]" />
            </span>
          )}
        </div>
        <div className={`text-[9px] uppercase tracking-widest font-bold ${dark ? "text-white/55" : "text-[#2d2c35]/60"} truncate max-w-full`}>
          {label}
        </div>
        <div className={`text-xl sm:text-2xl font-black font-mono-num tabular-nums leading-none ${
          dark ? "text-white" : "text-[#182138]"
        }`}>
          {fmt(value, lang)}
        </div>
        {sublabel && (
          <div className={`text-[9px] ${dark ? "text-[#18c98f]" : "text-[#12a777]"} font-bold inline-flex items-center gap-1 truncate max-w-full`}>
            <TrendingUp className="h-2.5 w-2.5 shrink-0" />
            <span className="truncate">{sublabel}</span>
          </div>
        )}
      </div>
    </div>
  );

  const StatsGrid = (
    <div data-testid="visitor-stats-grid" className="grid grid-cols-3 gap-2 sm:gap-3">
      <BigStat
        testId="stat-total"
        icon={Globe}
        label={ar ? "إجمالي الزوار" : "Total"}
        value={total}
        sublabel={ar ? "منصة متنامية" : "Growing"}
      />
      <BigStat
        testId="stat-today"
        icon={Eye}
        label={ar ? "زوار اليوم" : "Today"}
        value={today}
        sublabel={ar ? "منذ منتصف الليل" : "Since midnight"}
      />
      <BigStat
        testId="stat-online"
        icon={Users}
        label={ar ? "متصل الآن" : "Online"}
        value={online}
        sublabel={ar ? "يستخدمون الحاسبة" : "Using now"}
        pulse
      />
    </div>
  );

  // Compact embedded mode — just the 3-cards strip + tiny "منصة نشطة" pill.
  // Used inside the pricing hero so the hero and live-stats share one frame.
  if (embed) {
    return (
      <div data-testid="visitor-counter-embed" dir={ar ? "rtl" : "ltr"} className={font}>
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#18c98f]/10 border border-[#18c98f]/30 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-[0.2em] text-[#18c98f]">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#18c98f] opacity-70" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#18c98f]" />
            </span>
            {ar ? "منصة نشطة" : "Live"}
          </span>
        </div>
        {StatsGrid}
      </div>
    );
  }

  return (
    <section
      data-testid="visitor-counter"
      dir={ar ? "rtl" : "ltr"}
      className={`${font} ${dark ? "bg-[#182138]" : "bg-[#f8f8fa]"}`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-7">
        {/* Eyebrow + title in a compact header */}
        <div className="flex items-center justify-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#18c98f]/10 border border-[#18c98f]/30 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-[0.2em] text-[#18c98f]">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#18c98f] opacity-70" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#18c98f]" />
            </span>
            {ar ? "منصة نشطة" : "Live platform"}
          </span>
        </div>
        <h2 className={`text-center text-sm sm:text-base font-bold tracking-tight mb-3 ${dark ? "text-white" : "text-[#182138]"}`}>
          {ar
            ? "آلاف المستقلين السعوديين يثقون بـ برولانسر"
            : "Thousands of Saudi freelancers trust Prolancer"}
        </h2>
        {StatsGrid}
      </div>
    </section>
  );
}
